import { AnimatePresence, motion } from 'motion/react';
import type { ReactNode } from 'react';

// The house collapse. ONE implementation, deliberately.
//
// These lived as locals inside brand.$slug.campaigns.new.review.$id.tsx and
// were used by the ad tiles there. Being hard to find is not the same as not
// existing: the campaign page shipped a "why i say that" disclosure built on a
// bare <details>, which the browser snaps open with no transition, beside ad
// tiles that spring and fade. The founder spotted the difference immediately —
// a hand-rolled version of an existing component does not just duplicate code,
// it ships a visibly worse version of something the product already does well.
//
// Extracted rather than copied, so both callers move together.

/** Animated collapse wrapper for a detail body.
 *
 *  Uses motion's height: 'auto' so the container absorbs arbitrary content —
 *  an image, a keyword list, a whole rollup — without anyone measuring.
 *  Unmounts on collapse so the DOM matches what the founder is looking at. */
export function CollapsibleBlock({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          style={{ overflow: 'hidden' }}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/** The chevron, as its own round button. For surfaces where the toggle is a
 *  small control beside other controls — the ad tiles' header row. */
export function CollapseToggle({
  collapsed,
  onToggle,
  ariaLabel,
}: {
  collapsed: boolean;
  onToggle: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={ariaLabel}
      aria-expanded={!collapsed}
      className="-mr-1 ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[var(--ink)]/55 transition-colors hover:bg-[var(--spell-soft)]/60 hover:text-[var(--ink)]"
    >
      <CollapseChevron collapsed={collapsed} />
    </button>
  );
}

/** The chevron alone, for surfaces where the WHOLE ROW is the control and a
 *  nested <button> would be both wrong semantically and un-clickable in the
 *  right places. Same spring as CollapseToggle's, so the two read as one
 *  motion vocabulary rather than two. */
export function CollapseChevron({ collapsed }: { collapsed: boolean }) {
  return (
    <motion.svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      animate={{ rotate: collapsed ? 0 : 180 }}
      transition={{ type: 'spring', stiffness: 380, damping: 28 }}
    >
      <polyline points="6 9 12 15 18 9" />
    </motion.svg>
  );
}
